import React from "react";
import { Link } from "react-router-dom";
import { Trash2, MapPin, Building2, Landmark, ArrowUpRight } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Skeleton } from "components/ui/skeleton";
import { state_select } from "Explore/Config/FilterData";
import { ImageType, ImageCheckType } from "Explore/Config/ImageType";
import { ImageCheck } from "Explore/Components";

const getStateLabel = (stateVal) => {
  if (!stateVal) return "";
  const found = state_select.find((s) => s.value === stateVal);
  return found?.label || stateVal;
};

const FavoritesGridView = ({
  items,
  loading,
  entityType,
  onRemove,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="border-slate-200/80 shadow-xs bg-white rounded-xl">
            <CardContent className="p-5 flex flex-col gap-3">
              <div className="flex gap-3.5 items-center">
                <Skeleton className="h-16 w-16 rounded-xl" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
              <div className="flex gap-1.5 mt-1">
                <Skeleton className="h-5 w-20 rounded-md" />
                <Skeleton className="h-5 w-24 rounded-md" />
              </div>
              <Skeleton className="h-8 w-full rounded-lg mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const getItemLink = (item) => {
    const slug = item.explore_slug || item.id;
    if (entityType === "researchers") return `/explore/faculty/${slug}`;
    if (entityType === "units") return `/explore/unit/${slug}`;
    if (entityType === "venues") return `/explore/venue/${item.id}`;
    return `/explore/institution/${slug}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
      {items.map((item) => {
        const link = getItemLink(item);
        const keywords = Array.isArray(item.keywords)
          ? item.keywords.filter(Boolean)
          : [];

        return (
          <Card
            key={item.id}
            className="border-slate-200/90 hover:border-explore/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 bg-white flex flex-col justify-between group overflow-hidden rounded-xl shadow-xs"
          >
            <CardContent className="p-5 flex flex-col justify-between h-full">
              {/* RESEARCHERS */}
              {entityType === "researchers" && (
                <div className="flex flex-col">
                  {/* Top: Avatar & Info */}
                  <div className="flex items-start gap-3.5 mb-3">
                    <Link
                      to={link}
                      className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100 border border-slate-200/80 shadow-xs ring-2 ring-slate-100 group-hover:ring-explore/20 transition-all block"
                    >
                      <ImageCheck
                        url={item.photo_url}
                        type={ImageCheckType.FACULTY_AVATAR}
                      />
                    </Link>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <Link
                          to={link}
                          className="font-bold text-[15px] text-explore group-hover:text-explore/80 transition-colors leading-snug"
                          title={item.name}
                        >
                          {item.name}
                        </Link>
                        <button
                          type="button"
                          onClick={() => onRemove(item.id)}
                          className="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0 opacity-80 group-hover:opacity-100"
                          title="Remove from favorites"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      {item.position && (
                        <p
                          className="text-xs text-explore font-medium leading-snug mt-1 break-words"
                          title={item.position}
                        >
                          {item.position}
                        </p>
                      )}
                      {item.institution_name && (
                        <p
                          className="text-xs text-slate-500 font-medium leading-snug mt-1 flex items-start gap-1 break-words"
                          title={item.institution_name}
                        >
                          <Building2 size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
                          <span>{item.institution_name}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Research Area Badges */}
                  {keywords.length > 0 && (
                    <div className="mt-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                        Research Area
                      </span>
                      <div className="flex flex-wrap gap-1.5 max-h-14 overflow-hidden">
                        {keywords.slice(0, 3).map((kw, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="text-[11px] font-medium bg-slate-100 hover:bg-explore/10 hover:text-explore text-slate-600 border border-slate-200/60 rounded-md py-0.5 px-2 transition-colors truncate max-w-[200px]"
                          >
                            {kw}
                          </Badge>
                        ))}
                        {keywords.length > 3 && (
                          <span className="text-[10px] font-semibold text-slate-400 self-center">
                            +{keywords.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* INSTITUTIONS */}
              {entityType === "institutions" && (
                <div className="flex flex-col gap-3.5">
                  {/* Top Header: Seal Logo, Name, Location & Actions */}
                  <div className="flex items-start gap-3">
                    <Link
                      to={link}
                      className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-white rounded-xl border border-slate-200/90 p-1.5 shadow-2xs ring-2 ring-slate-100 group-hover:ring-explore/20 group-hover:border-explore/30 transition-all block"
                    >
                      <ImageCheck
                        url={`https://media.academic.online/institutions_images/${item.id}/${ImageType.SEAL}.png`}
                        type={ImageCheckType.INSTITUTION_LOGO}
                      />
                    </Link>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1.5">
                        <Link
                          to={link}
                          className="font-bold text-[16px] text-explore group-hover:text-explore/80 transition-colors leading-snug break-words"
                          title={item.name}
                        >
                          {item.name}
                        </Link>
                        <button
                          type="button"
                          onClick={() => onRemove(item.id)}
                          className="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0 opacity-80 group-hover:opacity-100"
                          title="Remove from favorites"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      <p className="text-xs text-slate-500 font-medium leading-snug mt-1 flex items-center gap-1">
                        <MapPin size={12} className="text-explore flex-shrink-0" />
                        <span>{[item.city, getStateLabel(item.state)].filter(Boolean).join(", ") || "United States"}</span>
                      </p>
                    </div>
                  </div>

                  {/* Top Subject Rankings */}
                  <div className="space-y-1.5 mt-0.5">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Top Academic Rankings
                    </span>

                    {Array.isArray(item.top_ranking_keywords) && item.top_ranking_keywords.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {item.top_ranking_keywords.slice(0, 3).map((kw, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-slate-50/80 hover:bg-slate-100/70 border border-slate-100 transition-colors text-xs"
                          >
                            <span className="font-medium text-slate-700 capitalize truncate" title={kw.keyword}>
                              {kw.keyword}
                            </span>
                            <span className="font-bold text-explore text-[11px] bg-explore/10 px-2 py-0.5 rounded-md border border-explore/15 flex-shrink-0 ml-2 tabular-nums">
                              #{kw.ranking}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-3 px-3 bg-slate-50/70 rounded-lg border border-dashed border-slate-200 text-center">
                        <p className="text-xs text-slate-400 italic m-0">Leading global research institution</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* UNITS */}
              {entityType === "units" && (
                <div className="flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <Link
                      to={link}
                      className="font-bold text-[16px] text-explore group-hover:text-explore/80 transition-colors line-clamp-1"
                      title={item.name}
                    >
                      {item.name}
                    </Link>
                    <button
                      type="button"
                      onClick={() => onRemove(item.id)}
                      className="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0 opacity-80 group-hover:opacity-100"
                      title="Remove from favorites"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {item.institution_name && (
                    <p className="text-xs text-explore font-medium truncate mb-2 flex items-center gap-1">
                      <Landmark size={12} className="text-explore flex-shrink-0" />
                      <span>{item.institution_name}</span>
                    </p>
                  )}

                  {/* Research Area & Landscape image */}
                  <div className="flex items-start justify-between gap-2 mt-2">
                    <div className="flex-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                        Research Area
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {keywords.slice(0, 3).map((kw, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="text-[11px] font-medium bg-slate-100 hover:bg-explore/10 hover:text-explore text-slate-600 border border-slate-200/60 rounded-md py-0.5 px-2 transition-colors truncate max-w-[180px]"
                          >
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {item.institution_id && (
                      <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-white border border-slate-200 p-0.5 shadow-xs">
                        <ImageCheck
                          url={`https://media.academic.online/institutions_images/${item.institution_id}/${ImageType.LANDSCAPE}.png`}
                          type={ImageCheckType.INSTITUTION_LOGO}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* VENUES */}
              {entityType === "venues" && (
                <div className="flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Link
                      to={link}
                      className="font-bold text-[16px] text-explore group-hover:text-explore/80 transition-colors line-clamp-1"
                      title={item.name}
                    >
                      {item.name}
                    </Link>
                    <button
                      type="button"
                      onClick={() => onRemove(item.id)}
                      className="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0 opacity-80 group-hover:opacity-100"
                      title="Remove from favorites"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {/* Research Area Badges */}
                  {keywords.length > 0 && (
                    <div className="mt-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                        Research Area
                      </span>
                      <div className="flex flex-wrap gap-1.5 max-h-14 overflow-hidden">
                        {keywords.slice(0, 3).map((kw, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="text-[11px] font-medium bg-slate-100 hover:bg-explore/10 hover:text-explore text-slate-600 border border-slate-200/60 rounded-md py-0.5 px-2 transition-colors truncate max-w-[200px]"
                          >
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CARD BOTTOM ACTION FOOTER */}
              <div className="pt-3.5 mt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] font-medium text-slate-400 capitalize">
                  {entityType === "researchers"
                    ? "Faculty Profile"
                    : entityType === "units"
                    ? "Department"
                    : entityType === "venues"
                    ? "Publication Venue"
                    : "Institution"}
                </span>

                <Link
                  to={link}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-explore hover:text-explore/80 group-hover:translate-x-0.5 transition-all"
                >
                  <span>View Details</span>
                  <ArrowUpRight size={13} />
                </Link>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default FavoritesGridView;
